import { useAtom } from "jotai";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { locale } from "../../../../store/localeStore";
import type { SupportedLanguage } from "../../constants/locale/types";
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
} from "../../constants/locale/types";

function normalize(candidate: string | null | undefined): SupportedLanguage {
  if (!candidate) return DEFAULT_LANGUAGE;
  const short = candidate.split("-")[0].toLowerCase();
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(short)
    ? (short as SupportedLanguage)
    : DEFAULT_LANGUAGE;
}

// 호출부 마이그레이션 전까지 atom 은 ko/en 만 담는다. ja/km 은 ko 로 좁혀 저장하되
// i18next 인스턴스에는 실제 언어 코드를 전달한다. §5(정리) 단계에서 atom 을
// SupportedLanguage 로 확장하면서 이 좁힘 로직은 제거된다.
function narrowForAtom(language: SupportedLanguage): "ko" | "en" {
  return language === "en" ? "en" : "ko";
}

export default function useLocale() {
  const [localeState, setLocaleState] = useAtom(locale);
  const { i18n } = useTranslation();

  useEffect(() => {
    // i18n.ts 에서 lng: DEFAULT_LANGUAGE 로 고정 초기화하므로
    // i18n.language 는 항상 DEFAULT_LANGUAGE 다. localStorage 를 직접 읽어
    // 사용자가 저장한 언어를 i18n + atom 에 반영한다.
    const stored =
      typeof window !== "undefined"
        ? window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
        : null;
    const detected = normalize(stored);
    const narrowed = narrowForAtom(detected);
    if (narrowed !== localeState.locale) {
      setLocaleState({ locale: narrowed });
    }
    if (detected !== i18n.language) {
      void i18n.changeLanguage(detected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 주의: atom → i18n 강제 동기화 useEffect 는 두지 않는다. atom 이
  // ko/en 만 담는 narrowed 상태라 사용자가 ja/km 을 선택해도 다음 render 에
  // atom 값(ko) 으로 i18n.language 가 덮어써져 ja/km 선택이 즉시 ko 로
  // 되돌아간다. atom 갱신은 `changeLocaleState` 안에서 i18n.changeLanguage
  // 와 함께 일어나므로 별도 useEffect 동기화는 불필요.

  const changeLocaleState = useCallback(
    ({ locale: next }: { locale: SupportedLanguage }) => {
      setLocaleState({ locale: narrowForAtom(next) });
      void i18n.changeLanguage(next);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
      }
    },
    [i18n, setLocaleState]
  );

  return [localeState, changeLocaleState] as const;
}
